import { ParserResult } from '@util';
import { Entity } from '@util/entity-parser/entity';
import { RawEntity } from '@util/entity-parser/raw-entity';
import * as R from 'ramda';

export interface EntityFactoryParams<T extends RawEntity> {
  rawEntity: T;
  entity: Entity;
  parsedEntityId: string[];
}

type EntityFactory<T extends RawEntity, E extends Entity> = (input: EntityFactoryParams<T>) => E;

const ENTITY_NAME_POSITION = 0;
const ENTITY_ID_POSITION = 1;
const PROPERTY_POSITION = 2;
const ENTITY_ID_SEPARATOR = '/';

// EntityName/:id/field
type ParseEntityId = (entityId: string) => string[];
export const parseEntityId: ParseEntityId = R.split(ENTITY_ID_SEPARATOR);

type GetEntityId = (xs: string[]) => number;
export const getEntityId: GetEntityId = R.compose(Number, R.nth(ENTITY_ID_POSITION));

type GetEntityName = (xs: string[]) => string;
export const getEntityName: GetEntityName = R.nth(ENTITY_NAME_POSITION);

type GetPropertyName = (xs: string[]) => string;
export const getPropertyName: GetPropertyName = R.nth(PROPERTY_POSITION);

type GroupByEntity = <T extends Entity>(rules: T[]) => {[key: string]: T[]};
const groupByEntity: GroupByEntity = R.groupBy(R.prop('entity'));

type GroupByEntityId = <T extends Entity>(rules: T[]) => {[key: number]: T[]};
const groupByEntityId: GroupByEntityId = R.groupBy(R.prop('entityId'));

const createEntity = (entityIdFields: string[]): Entity => ({
  entity: getEntityName(entityIdFields),
  entityId: getEntityId(entityIdFields),
  propertyName: getPropertyName(entityIdFields)
});

const createFactoryInput = <T extends RawEntity>(rawEntity: T): EntityFactoryParams<T> => {
  const entityIdFields = parseEntityId(rawEntity.id);

  return {
    rawEntity,
    entity: createEntity(entityIdFields),
    parsedEntityId: entityIdFields
  };
};

const createFactoryInputs = R.map<RawEntity, EntityFactoryParams<any>>(createFactoryInput);

type ByEntityId = <E extends Entity>(rules: {[key: string]: E[]}) => {[key: string]: {[key: number]: E[]}};
const byEntityId: ByEntityId = R.map(groupByEntityId);

export const parseAsArray = <T extends RawEntity, E extends Entity>(entityFactory: EntityFactory<T, E>) => (entities: T[]): E[] => {
  const inputs: EntityFactoryParams<T>[] = createFactoryInputs(entities);
  return R.map(entityFactory, inputs);
};

export const parse = <T extends RawEntity, E extends Entity>(entityFactory: EntityFactory<T, E>) => (entities: T[]): ParserResult<E> => {
  const resultList = parseAsArray(entityFactory)(entities);
  const byEntity = groupByEntity(resultList);

  return byEntityId(byEntity);
};
